import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { motion } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';


export default function CheckoutPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  // For demonstration, we'll simulate a logged-in user after authentication
  const [isLoaded, setIsLoaded] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(true);
  
  const [packageInfo, setPackageInfo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Get package information from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const packageId = searchParams.get('package');
    const price = searchParams.get('price');
    const name = searchParams.get('name');
    
    if (packageId && price && name) {
      setPackageInfo({
        id: packageId,
        price,
        name
      });
    } else {
      setError('Missing package information. Please select a package first.');
    }
    
    // Set page as loaded after a short delay for entrance animation
    setTimeout(() => {
      setPageLoaded(true);
    }, 300);
  }, [location.search]);
  
  // In a real implementation, we would check for authentication like this:
  /*
  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      const redirectUrl = `/checkout${location.search}`;
      navigate(`/sign-in?redirect=${encodeURIComponent(redirectUrl)}`);
    }
  }, [isLoaded, isSignedIn, navigate, location.search]);
  */
  
  const handleProceedToPayment = () => {
    if (!packageInfo) return;
    
    setIsProcessing(true);
    
    // Create payment URL with package info
    const params = new URLSearchParams({
      package: packageInfo.id,
      price: packageInfo.price,
      name: packageInfo.name
    });
    
    // Add a short delay for visual feedback before navigating
    setTimeout(() => {
      navigate(`/payment?${params.toString()}`);
    }, 600);
  };
  
  // Show loading state while checking authentication
  if (!isLoaded || !packageInfo) {
    return (
      <div className="min-h-screen">
        <Navbar /> 
        <main className="checkout-page">
          <div className="loading-container">
            <div className="loading-animation">
              <div className="loading-bar"></div>
              <div className="loading-bar"></div>
              <div className="loading-bar"></div>
            </div>
          </div>
        </main>
        
        <style jsx>{`
          .checkout-page {
            background: var(--light, #f9f9f9);
            color: var(--text-dark, #333);
            padding: 8rem 2rem;
            min-height: 80vh;
          }
          
          .loading-container {
            max-width: 800px;
            margin: 0 auto;
            text-align: center;
          }
          
          .loading-animation {
            padding: 3rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }
          
          .loading-bar {
            height: 1rem;
            background-color: rgba(106, 62, 161, 0.2);
            border-radius: 0.5rem;
            margin-bottom: 0.5rem;
            animation: pulse 1.5s infinite;
          }
          
          .loading-bar:nth-child(1) {
            width: 25%;
          }
          
          .loading-bar:nth-child(2) {
            width: 50%;
          }
          
          .loading-bar:nth-child(3) {
            width: 33%;
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 0.5;
            }
            50% {
              opacity: 1;
            }
          }
        `}</style>
        
        <Footer />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar /> 
        <main className="checkout-page">
          <div className="error-container">
            <h2 className="error-title">Error</h2>
            <p className="error-message">{error}</p>
            <button
              onClick={() => navigate('/pricing')}
              className="return-button"
            >
              Return to Pricing
            </button>
          </div>
        </main>
        
        <style jsx>{`
          .checkout-page {
            background: var(--light, #f9f9f9);
            color: var(--text-dark, #333);
            padding: 8rem 2rem;
            min-height: 80vh;
            text-align: center;
          }
          
          .error-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #fef2f2;
            border-radius: 1rem;
            padding: 2rem;
            color: #b91c1c;
          }
          
          .error-title {
            font-family: var(--serif, serif);
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
          }
          
          .error-message {
            margin-bottom: 1.5rem;
          }
          
          .return-button {
            background: var(--primary, #6A3EA1);
            color: white;
            font-size: 1rem;
            font-weight: 600;
            padding: 0.8rem 1.8rem;
            border-radius: 50px;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
            display: inline-block;
          }
          
          .return-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(106, 62, 161, 0.3);
          }
        `}</style>
        
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      <Navbar /> 
      <main className="checkout-page">
        <div>
          {/* Progress indicators */}
          <div className="progress-container">
            <div className="progress-steps">
              <div className="progress-step">
                <div className="step-circle completed">1</div>
                <span className="step-label">Choose Plan</span>
              </div>
              <div className="progress-line active"></div>
              <div className="progress-step">
                <div className="step-circle active">2</div>
                <span className="step-label">Review</span>
              </div>
              <div className={`progress-line ${isProcessing ? 'active' : ''}`}></div>
              <div className="progress-step">
                <div className={`step-circle ${isProcessing ? 'active' : ''}`}>3</div>
                <span className="step-label">Payment</span>
              </div>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: pageLoaded ? 1 : 0, y: pageLoaded ? 0 : 20 }}
            transition={{ duration: 0.5 }}
            className="checkout-header"
          >
            <h1 className="section-title">Review Your Order</h1>
            <p className="subtitle">
              Please review your order details before proceeding to payment
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: pageLoaded ? 1 : 0, y: pageLoaded ? 0 : 30 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="order-summary"
          >
            <div className="order-content">
              <div className="order-details">
                <div className="order-info">
                  <h2 className="order-title">WizardPortrait {packageInfo.name}</h2>
                  <p className="order-type">Subscription plan, billed annually</p>
                  
                  {isSignedIn && user && (
                    <div className="user-info">
                      <h3 className="user-info-title">Account Information</h3>
                      <p className="user-name">{user.fullName || 'User'}</p>
                      <p className="user-email">{user.primaryEmailAddress?.emailAddress}</p>
                    </div>
                  )}
                </div>
                <div className="order-price">
                  <span>${packageInfo.price}</span>
                </div>
              </div>
              
              <div className="price-breakdown">
                <div className="subtotal">
                  <span>Subtotal</span>
                  <span>${packageInfo.price}</span>
                </div>
                <div className="total">
                  <span>Total</span>
                  <span>${packageInfo.price}</span>
                </div>
              </div>
              
              <div className="action-container">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleProceedToPayment}
                  disabled={isProcessing}
                  className={`checkout-button ${isProcessing ? 'processing' : ''}`}
                >
                  {isProcessing ? (
                    <div className="processing-container">
                      <div className="spinner"></div>
                      Processing...
                    </div>
                  ) : 'Proceed to Payment'}
                </motion.button>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: pageLoaded ? 1 : 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="return-link-container"
          >
            <button
              onClick={() => navigate('/pricing')}
              className="return-link"
            >
              &larr; Return to Pricing
            </button>
          </motion.div>
        </div>
      </main>
      
      <style jsx>{`
        .checkout-page {
          background: var(--light, #f9f9f9);
          color: var(--text-dark, #333);
          padding: 8rem 2rem;
          text-align: center;
        }
        
        .section-title {
          font-family: var(--serif, serif);
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 1rem;
          position: relative;
          display: inline-block;
          font-weight: 600;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          width: 80px;
          height: 4px;
          background: var(--accent, #F9B932);
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .subtitle {
          font-size: 1.2rem;
          max-width: 700px;
          margin: 2rem auto 3rem;
        }
        
        .progress-container {
          margin: 3rem auto;
          max-width: 600px;
        }
        
        .progress-steps {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary, #6A3EA1);
          opacity: 0.5;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .step-circle.active, .step-circle.completed {
          opacity: 1;
        }
        
        .step-label {
          margin-top: 8px;
          font-size: 0.9rem;
        }
        
        .progress-line {
          flex: 1;
          height: 2px;
          background: var(--secondary, #9857D3);
          opacity: 0.3;
          margin: 0 10px;
        }
        
        .progress-line.active {
          opacity: 1;
        }
        
        .checkout-header {
          margin-bottom: 3rem;
        }
        
        .order-summary {
          max-width: 800px;
          margin: 0 auto 2rem;
          background: white;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }
        
        .order-content {
          padding: 2rem;
        }
        
        .order-details {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
        }
        
        .order-info {
          text-align: left;
        }
        
        .order-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          font-family: var(--serif, serif);
        }
        
        .order-type {
          color: var(--text-muted, #666);
          margin-bottom: 1rem;
        }
        
        .order-price {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--primary, #6A3EA1);
        }
        
        .user-info {
          margin-top: 1.5rem;
          text-align: left;
        }
        
        .user-info-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .user-name, .user-email {
          margin-bottom: 0.25rem;
        }
        
        .price-breakdown {
          border-top: 1px solid rgba(0, 0, 0, 0.1);
          padding-top: 1.5rem;
          margin-bottom: 1.5rem;
        }
        
        .subtotal, .total {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          text-align: left;
        }
        
        .total {
          font-weight: 700;
          font-size: 1.2rem;
          margin-top: 0.5rem;
        }
        
        .action-container {
          margin-top: 2rem;
        }
        
        .checkout-button {
          background: var(--accent, #F9B932);
          color: var(--text-dark, #333);
          font-size: 1rem;
          font-weight: 600;
          padding: 1rem 2rem;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 1px;
          width: 100%;
        }
        
        .checkout-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(249, 185, 50, 0.3);
        }
        
        .checkout-button.processing {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .processing-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .spinner {
          width: 1.25rem;
          height: 1.25rem;
          border: 2px solid rgba(0, 0, 0, 0.3);
          border-top-color: var(--text-dark, #333);
          border-radius: 50%;
          margin-right: 0.75rem;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        .return-link-container {
          margin-top: 2rem;
        }
        
        .return-link {
          color: var(--primary, #6A3EA1);
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .return-link:hover {
          color: var(--secondary, #9857D3);
        }
        
        @media (max-width: 768px) {
          .order-details {
            flex-direction: column;
          }
          
          .order-price {
            margin-top: 1rem;
            text-align: left;
          }
          
          .section-title {
            font-size: 2rem;
          }
        }
      `}</style>
      
      <Footer />
    </div>
  );
} 